import { useState, useEffect, useCallback } from "react";
import { GestionHeader } from "./SiteHeader";
import CreateOrgModal from "./CreateOrgModal";
import EditOrgModal from "./EditOrgModal";
import { apiGet, apiFetch, errorMessage } from "./api";
import "./OrgManagement.css";

function OrgManagement({ user, onNavigate, onLogout }) {
  const [orgs, setOrgs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchOrgs = useCallback(async (signal) => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 20 });
    if (search) params.append("search", search);

    try {
      const data = await apiGet(`/api/organizations?${params}`, "Impossible de charger les organisations");
      if (signal?.aborted) return;
      setOrgs(data.organizations);
      setTotalPages(data.pages);
      setTotal(data.total);
      setError("");
    } catch (e) {
      if (signal?.aborted) return;
      setOrgs([]);
      setTotal(0);
      setTotalPages(1);
      setError(errorMessage(e, "Impossible de charger les organisations"));
    }
    if (!signal?.aborted) setLoading(false);
  }, [page, search]);

  // Debounce : une requête par frappe sinon, avec réponses dans le désordre
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => fetchOrgs(controller.signal), 300);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [fetchOrgs]);

  const deleteOrg = async (o) => {
    if (!window.confirm(
      `Supprimer définitivement « ${o.name} » ?\n\n` +
      `Seront aussi supprimés : ses ${o.card_count} carte${o.card_count > 1 ? "s" : ""} ` +
      "de fidélité (avec leur historique), ses comptes gérant et employés, et " +
      "son abonnement Stripe sera annulé. Cette action est irréversible."
    )) {
      return;
    }
    setDeletingId(o.id);
    try {
      await apiFetch(`/api/organizations/${o.id}`, { method: "DELETE" },
        "Impossible de supprimer l'organisation");
      setError("");
      await fetchOrgs();
    } catch (e) {
      setError(errorMessage(e, "Impossible de supprimer l'organisation"));
    }
    setDeletingId(null);
  };

  return (
    <div className="om-container">
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="Gestion Organisations"
        onBack={() => onNavigate("admin")}
        onHome={() => onNavigate("home")}
        onLogout={onLogout}
      />

      <main className="om-main">
        <div className="om-filters">
          <input
            type="text"
            className="om-search"
            placeholder="Rechercher une organisation..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        <div className="om-toolbar">
          <p className="om-count">{total} organisation{total > 1 ? "s" : ""}</p>
          <button className="om-btn-create" onClick={() => setShowCreate(true)}>+ Créer une organisation</button>
        </div>

        {error && <div className="om-error">{error}</div>}

        {loading ? (
          <p className="om-loading">Chargement...</p>
        ) : orgs.length === 0 ? (
          <p className="om-empty">Aucune organisation trouvée</p>
        ) : (
          <>
            <div className="om-table-wrapper">
              <table className="om-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nom</th>
                    <th>SIRET</th>
                    <th>Cartes</th>
                    <th>Paiement</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((o) => (
                    <tr key={o.id}>
                      <td>{o.id}</td>
                      <td>{o.name}</td>
                      <td className="om-siret">{o.siret || "—"}</td>
                      <td><span className="om-card-count">{o.card_count}</span></td>
                      <td>
                        <span className={`om-pay${o.payment_default ? " om-pay-late" : ""}`}>
                          {o.payment_default ? "En défaut" : "À jour"}
                        </span>
                      </td>
                      <td className="om-actions">
                        <button className="om-btn-edit" onClick={() => setEditingOrg(o)}>Modifier</button>
                        <button
                          className="om-btn-delete"
                          onClick={() => deleteOrg(o)}
                          disabled={deletingId !== null}
                        >
                          {deletingId === o.id ? "Suppression..." : "Supprimer"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="om-cards">
              {orgs.map((o) => (
                <div className="om-card" key={o.id}>
                  <div className="om-card-header">
                    <strong>{o.name}</strong>
                    <span className="om-card-count">{o.card_count} carte{o.card_count > 1 ? "s" : ""}</span>
                  </div>
                  <button className="om-btn-edit om-btn-edit-mobile" onClick={() => setEditingOrg(o)}>Modifier</button>
                  <button
                    className="om-btn-delete om-btn-edit-mobile"
                    onClick={() => deleteOrg(o)}
                    disabled={deletingId !== null}
                  >
                    {deletingId === o.id ? "Suppression..." : "Supprimer"}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {totalPages > 1 && (
          <div className="om-pagination">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Précédent</button>
            <span>{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Suivant</button>
          </div>
        )}

        {showCreate && (
          <CreateOrgModal
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); setPage(1); fetchOrgs(); }}
          />
        )}
        {editingOrg && (
          <EditOrgModal
            orgToEdit={editingOrg}
            canSetPayment={user.role === "Admin"}
            onClose={() => setEditingOrg(null)}
            onUpdated={() => { setEditingOrg(null); fetchOrgs(); }}
          />
        )}
      </main>
    </div>
  );
}

export default OrgManagement;
